import { useState, useEffect } from 'react'
import { TemporaryAircraftStateService } from '../services/temporary-aircraft-state.service'

export function useMarketMovement(modelId: string | undefined) {
  // Track aircraft IDs that have been temporarily moved to/from containers (for accurate tab counts)
  // Excluded = aircraft moved AWAY from this container (subtract from count)
  // Added = aircraft moved INTO this container (add to count)
  const [excludedFromForSale, setExcludedFromForSale] = useState<Set<number>>(new Set())
  const [excludedFromSold, setExcludedFromSold] = useState<Set<number>>(new Set())
  const [excludedFromRemoved, setExcludedFromRemoved] = useState<Set<number>>(new Set())
  const [addedToForSale, setAddedToForSale] = useState<number>(0)
  const [addedToSold, setAddedToSold] = useState<number>(0)
  const [addedToRemoved, setAddedToRemoved] = useState<number>(0)

  // Fetch excluded/added aircraft for accurate tab counts (aircraft temporarily moved between containers)
  useEffect(() => {
    const fetchTemporaryAircraftCounts = async () => {
      if (!modelId) return

      const numericModelId = parseInt(modelId, 10)
      if (isNaN(numericModelId)) return

      try {
        // Fetch aircraft to exclude (moved AWAY from each container)
        const [forSaleExcluded, soldExcluded, removedExcluded] = await Promise.all([
          TemporaryAircraftStateService.getAircraftIdsToExclude(numericModelId, 'for_sale'),
          TemporaryAircraftStateService.getAircraftIdsToExclude(numericModelId, 'sold'),
          TemporaryAircraftStateService.getAircraftIdsToExclude(numericModelId, 'removed_from_market')
        ])

        setExcludedFromForSale(new Set(forSaleExcluded))
        setExcludedFromSold(new Set(soldExcluded))
        setExcludedFromRemoved(new Set(removedExcluded))

        // Fetch aircraft added (moved INTO each container)
        const [forSaleAdded, soldAdded, removedAdded] = await Promise.all([
          TemporaryAircraftStateService.getTemporaryAircraftForContainer(numericModelId, 'for_sale'),
          TemporaryAircraftStateService.getTemporaryAircraftForContainer(numericModelId, 'sold'),
          TemporaryAircraftStateService.getTemporaryAircraftForContainer(numericModelId, 'removed_from_market')
        ])

        setAddedToForSale(forSaleAdded.length)
        setAddedToSold(soldAdded.length)
        setAddedToRemoved(removedAdded.length)
      } catch (error) {
        // Silently fail - tab counts will just not account for temp moves
      }
    }

    fetchTemporaryAircraftCounts()
  }, [modelId])

  return {
    excludedFromForSale,
    excludedFromSold,
    excludedFromRemoved,
    addedToForSale,
    addedToSold,
    addedToRemoved,
  }
}

